import { type JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { useGlobal } from '~/hooks/useGlobal';

import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs';
import type { BreadcrumbsItem } from '../../../stores/CatalogStore';

import MobileHeaderLayout from '../../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../../Main/MobileHeader/MobileHeaderTitle';

import styles from '../Legals.module.scss';

import mdStyles from './LegalsDubaiExpress.module.scss';

import '../styles.scss';

type Props = {
  handleLink: (anchor: string) => void;
};

const LEGALS_NAV_RU = [
  { link: 'privacy-policy', title: 'Политика (введение)' },
  { link: 'general-terms', title: '1. Общие положения' },
  { link: 'definitions', title: '2. Полезные определения' },
  { link: 'site-info', title: '3. О сайте и функциях' },
  { link: 'processing-goals', title: '4. Цели обработки' },
  { link: 'cookies', title: '5. Cookies' },
  { link: 'third-parties', title: '6. Передача третьим лицам' },
  { link: 'cross-border', title: '7. Трансграничная передача' },
  { link: 'protection', title: '8. Защита данных' },
  { link: 'user-rights', title: '9. Права пользователя' },
  { link: 'minors', title: '10. Несовершеннолетние' },
  { link: 'changes', title: '11. Изменения политики' },
  {
    link: 'intellectual-property',
    title: '12. Интеллектуальная собственность',
  },
];

const LEGALS_NAV_EN = [
  { link: 'privacy-policy', title: 'Privacy Policy (Introduction)' },
  { link: 'general-terms', title: '1. General Provisions' },
  { link: 'definitions', title: '2. Definitions' },
  { link: 'site-info', title: '3. About the Website' },
  { link: 'processing-goals', title: '4. Data Processing Purposes' },
  { link: 'cookies', title: '5. Cookies' },
  { link: 'third-parties', title: '6. Third-Party Data Sharing' },
  { link: 'cross-border', title: '7. Cross-Border Data Transfer' },
  { link: 'protection', title: '8. Data Protection' },
  { link: 'user-rights', title: '9. User Rights' },
  { link: 'minors', title: '10. Minors' },
  { link: 'changes', title: '11. Policy Changes' },
  { link: 'intellectual-property', title: '12. Intellectual Property' },
];

const POLICY_RU = `
<h2 id="privacy-policy">Политика конфиденциальности и обработки персональных данных</h2>
<p>dubaiexpress.ru</p>
<p>Версия 1 от 01.01.2026</p>

<hr />

<h3 id="general-terms">1. Общие положения</h3>

<h4 id="general-terms-1-1">1.1. Сайт</h4>
<p>dubaiexpress.ru («Сайт»)</p>

<h4 id="general-terms-1-2">1.2. Компания (оператор)</h4>
<p>
Сайтом управляет компания, зарегистрированная и осуществляющая деятельность
в свободной экономической зоне DAFZA ОАЭ («Компания»).
</p>

<h4 id="general-terms-1-3">1.3. Реквизиты Компании</h4>
<p>
DSO-IFZA, IFZA Properties,<br/>
Dubai Silicon Oasis,<br/>
United Arab Emirates<br/>
License number 38457
</p>

<h4 id="general-terms-1-4">1.4. Назначение документа</h4>
<p>Политика определяет:</p>
<ul>
    <li>порядок и условия обработки персональных данных;</li>
    <li>условия защиты интеллектуальной собственности.</li>
</ul>
<p>
Политика разработана для защиты прав и свобод пользователей Сайта
в соответствии с применимым законодательством.
</p>

<h4 id="general-terms-1-5">1.5. Стороны Политики</h4>
<p>
Политика регулирует отношения между пользователем Сайта и Компанией.
</p>

<h4 id="general-terms-1-6">1.6. Сфера действия Политики</h4>
<p>Политика распространяется на данные, получаемые Компанией:</p>
<ul>
    <li>при использовании Сайта и поддоменов;</li>
    <li>при использовании сервисов и форм;</li>
    <li>при заключении и исполнении договоров через Сайт.</li>
</ul>

<h4 id="general-terms-1-7">1.7. Согласие с Политикой</h4>
<p>
Используя Сайт, пользователь соглашается с условиями Политики.
При несогласии использование Сайта должно быть прекращено.
</p>

<hr />

<h3 id="definitions">2. Полезные определения</h3>

<h4 id="definitions-2-1">2.1. Персональные данные</h4>
<p>
Любая информация, прямо или косвенно относящаяся к пользователю Сайта.
</p>

<h4 id="definitions-2-2">2.2. Оператор персональных данных</h4>
<p>
Лицо, определяющее цели и способы обработки персональных данных.
</p>
<p>Компания является оператором персональных данных.</p>

<h4 id="definitions-2-3">2.3. Обработка персональных данных</h4>
<p>
Любое действие или совокупность действий с персональными данными, включая:
</p>
<p>
сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение,
использование, передачу, обезличивание, блокирование, удаление, уничтожение.
</p>

<h4 id="definitions-2-4">2.4. Применимое законодательство</h4>
<p>
Законодательство РФ применяется, если пользователь является гражданином РФ
и/или использует Сайт с территории РФ.
</p>

<hr />

<h3 id="site-info">3. Информация о Сайте и его функциях</h3>

<h4 id="site-info-3-1">3.1. Статус Сайта</h4>
<p>
Сайт является информационным ресурсом и предоставляет сведения о товарах,
реализуемых Компанией на маркетплейсе Ozon.ru.
</p>

<h4 id="site-info-3-2">3.2. Ограничения функционала</h4>
<p>Сайт не является интернет-магазином.</p>

<h4 id="site-info-3-3">3.3. Переход на Ozon.ru</h4>
<p>
Кнопка «Купить» перенаправляет пользователя на Ozon.ru,
где покупка совершается в соответствии с политиками Ozon.ru.
</p>
<p>
<a href="https://docs.ozon.ru/legal/personal-data/privacy-policy/" target="_blank" rel="nofollow">
Политика конфиденциальности Ozon.ru
</a>
</p>

<h4 id="site-info-3-4">3.4. Действия, которые Компания не осуществляет</h4>
<ul>
    <li>приём платежей;</li>
    <li>доставку товаров;</li>
    <li>управление заказами;</li>
    <li>обработку возвратов;</li>
    <li>сбор платёжных данных.</li>
</ul>

<h4 id="site-info-3-5">3.5. Действия, которые Компания осуществляет</h4>
<ul>
    <li>информирование о товарах;</li>
    <li>перенаправление на Ozon.ru;</li>
    <li>обработку запросов через формы;</li>
    <li>сбор аналитики.</li>
</ul>

<hr />

<h3 id="processing-goals">4. Цели, сроки и основания обработки персональных данных</h3>

<h4 id="processing-goals-4-1">4.1. Принципы обработки</h4>
<p>
Обработка осуществляется только для конкретных, законных и заранее определённых целей.
</p>

<h4 id="processing-goals-4-2">4.2. Ограничения обработки</h4>
<p>
Не допускается обработка данных, несовместимая с целями их сбора.
</p>

<h4 id="processing-goals-4-3">4.3. Обработка без согласия</h4>
<p>Компания вправе обрабатывать данные без согласия пользователя:</p>
<ul>
    <li>для исполнения договора;</li>
    <li>для заключения договора по инициативе пользователя;</li>
    <li>для защиты законных интересов Компании и соблюдения законодательства ОАЭ.</li>
</ul>

<hr />

<h3 id="cookies">5. Аналитика (Cookies)</h3>

<h4 id="cookies-5-1">5.1. Понятие cookies</h4>
<p>
Cookies — это небольшие текстовые файлы, сохраняемые на устройстве пользователя.
</p>

<h4 id="cookies-5-2">5.2. Цели использования cookies</h4>
<ul>
    <li>корректная работа Сайта;</li>
    <li>персонализация;</li>
    <li>аналитика;</li>
    <li>маркетинг.</li>
</ul>

<h4 id="cookies-5-3">5.3. Основание обработки</h4>
<p>
Обработка cookies осуществляется на основании согласия пользователя,
выраженного конклюдентными действиями.
</p>

<h4 id="cookies-5-4">5.4. Отказ от cookies</h4>
<p>
Пользователь может отключить cookies в настройках браузера,
что может повлечь ограничение функциональности Сайта.
</p>

<hr />

<h3 id="third-parties">6. Передача персональных данных третьим лицам</h3>

<h4 id="third-parties-6-1">6.1. Основания передачи</h4>
<p>
Передача данных осуществляется для целей, указанных в Политике.
</p>

<h4 id="third-parties-6-2">6.2. Перечень третьих лиц</h4>
<table border="1" cellpadding="6">
    <tr>
        <th>Третье лицо</th>
        <th>Цель</th>
        <th>Адрес</th>
    </tr>
    <tr>
        <td>ООО «Яндекс»</td>
        <td>Веб-аналитика (Яндекс Метрика)</td>
        <td>Россия, г. Москва, ул. Льва Толстого, д. 16</td>
    </tr>
</table>

<hr />

<h3 id="cross-border">7. Трансграничная передача персональных данных</h3>

<h4 id="cross-border-7-1">7.1. Общие положения</h4>
<p>
Компания осуществляет обработку данных на территории ОАЭ.
</p>

<h4 id="cross-border-7-2">7.2. Локализация данных граждан РФ</h4>
<p>
Первичная обработка персональных данных граждан РФ осуществляется на территории РФ.
</p>

<h4 id="cross-border-7-3">7.3. Законодательство ОАЭ</h4>
<p>
Federal Decree Law No. 45 of 2021 Regarding the Protection of Personal Data.
</p>

<hr />

<h3 id="protection">8. Защита персональных данных и ответственность</h3>

<h4 id="protection-8-1">8.1. Конфиденциальность</h4>
<p>
Персональные данные являются конфиденциальной информацией.
</p>

<h4 id="protection-8-2">8.2. Меры защиты</h4>
<p>
Компания применяет технические и организационные меры защиты данных.
</p>

<h4 id="protection-8-3">8.3. Ограничение ответственности</h4>
<p>
Компания не несёт ответственности за сайты третьих лиц и действия пользователя.
</p>

<hr />

<h3 id="user-rights">9. Права пользователя</h3>

<h4 id="user-rights-9-1">9.1. Перечень прав</h4>
<ul>
    <li>доступ к данным;</li>
    <li>уточнение;</li>
    <li>отзыв согласия;</li>
    <li>удаление данных;</li>
    <li>обжалование действий Компании.</li>
</ul>

<h4 id="user-rights-9-2">9.2. Порядок реализации прав</h4>
<p>
Запрос направляется на e-mail:
<a href="mailto:info@wbbs.ae">info@wbbs.ae</a>
</p>

<hr />

<h3 id="minors">10. Несовершеннолетние пользователи</h3>
<p>
Сайт не предназначен для лиц младше 18 лет.
Персональные данные несовершеннолетних подлежат удалению.
</p>

<hr />

<h3 id="changes">11. Изменения Политики</h3>
<p>
Новая редакция вступает в силу с момента публикации на Сайте.
</p>

<hr />

<h3 id="intellectual-property">12. Интеллектуальная собственность</h3>
<p>
Все права на Сайт принадлежат Компании.
Копирование и автоматизированный сбор данных запрещены.
</p>
`;

const POLICY_EN = `
<h2 id="privacy-policy">Privacy Policy and Personal Data Processing</h2>
<p>dubaiexpress.com</p>
<p>Version 1 dated 01.01.2026</p>

<hr />

<h3 id="general-terms">1. General Provisions</h3>

<h4 id="general-terms-1-1">1.1. Website</h4>
<p>dubaiexpress.com ("Website")</p>

<h4 id="general-terms-1-2">1.2. Company (Operator)</h4>
<p>
The Website is operated by a company registered and conducting business
in the DAFZA free economic zone, UAE ("Company").
</p>

<h4 id="general-terms-1-3">1.3. Company Details</h4>
<p>
DSO-IFZA, IFZA Properties,<br/>
Dubai Silicon Oasis,<br/>
United Arab Emirates<br/>
License number 38457
</p>

<h4 id="general-terms-1-4">1.4. Purpose of this Document</h4>
<p>This Policy defines:</p>
<ul>
    <li>the procedure and conditions for personal data processing;</li>
    <li>the conditions for intellectual property protection.</li>
</ul>
<p>
This Policy has been developed to protect the rights and freedoms of Website users
in accordance with applicable legislation.
</p>

<h4 id="general-terms-1-5">1.5. Parties to the Policy</h4>
<p>
This Policy governs the relationship between the Website user and the Company.
</p>

<h4 id="general-terms-1-6">1.6. Scope of the Policy</h4>
<p>This Policy applies to data received by the Company:</p>
<ul>
    <li>when using the Website and subdomains;</li>
    <li>when using services and forms;</li>
    <li>when entering into and fulfilling contracts through the Website.</li>
</ul>

<h4 id="general-terms-1-7">1.7. Consent to the Policy</h4>
<p>
By using the Website, the user agrees to the terms of this Policy.
If you do not agree, you must discontinue use of the Website.
</p>

<hr />

<h3 id="definitions">2. Definitions</h3>

<h4 id="definitions-2-1">2.1. Personal Data</h4>
<p>
Any information directly or indirectly relating to a Website user.
</p>

<h4 id="definitions-2-2">2.2. Personal Data Operator</h4>
<p>
A person who determines the purposes and methods of personal data processing.
</p>
<p>The Company is the personal data operator.</p>

<h4 id="definitions-2-3">2.3. Personal Data Processing</h4>
<p>
Any action or set of actions performed on personal data, including:
</p>
<p>
collection, recording, systematization, accumulation, storage, clarification, extraction,
use, transfer, anonymization, blocking, deletion, and destruction.
</p>

<h4 id="definitions-2-4">2.4. Applicable Legislation</h4>
<p>
UAE legislation applies to all users of the Website.
</p>

<hr />

<h3 id="site-info">3. About the Website and Its Functions</h3>

<h4 id="site-info-3-1">3.1. Website Status</h4>
<p>
The Website is an informational resource providing information about products
sold by the Company.
</p>

<h4 id="site-info-3-2">3.2. Functional Limitations</h4>
<p>The Website is not an online store.</p>

<h4 id="site-info-3-3">3.3. Purchase Process</h4>
<p>
The "Buy" button redirects the user to the respective marketplace,
where the purchase is completed in accordance with the marketplace policies.
</p>

<h4 id="site-info-3-4">3.4. Actions the Company Does Not Perform</h4>
<ul>
    <li>payment processing;</li>
    <li>product delivery;</li>
    <li>order management;</li>
    <li>returns processing;</li>
    <li>payment data collection.</li>
</ul>

<h4 id="site-info-3-5">3.5. Actions the Company Performs</h4>
<ul>
    <li>product information;</li>
    <li>redirection to marketplaces;</li>
    <li>processing requests through forms;</li>
    <li>analytics collection.</li>
</ul>

<hr />

<h3 id="processing-goals">4. Purposes, Terms, and Legal Basis for Personal Data Processing</h3>

<h4 id="processing-goals-4-1">4.1. Processing Principles</h4>
<p>
Processing is carried out only for specific, lawful, and predetermined purposes.
</p>

<h4 id="processing-goals-4-2">4.2. Processing Limitations</h4>
<p>
Processing of data incompatible with the purposes of collection is not permitted.
</p>

<h4 id="processing-goals-4-3">4.3. Processing Without Consent</h4>
<p>The Company may process data without the user's consent:</p>
<ul>
    <li>for contract performance;</li>
    <li>for entering into a contract at the user's initiative;</li>
    <li>for protecting the Company's legitimate interests and compliance with UAE legislation.</li>
</ul>

<hr />

<h3 id="cookies">5. Analytics (Cookies)</h3>

<h4 id="cookies-5-1">5.1. What Are Cookies</h4>
<p>
Cookies are small text files stored on the user's device.
</p>

<h4 id="cookies-5-2">5.2. Purposes of Using Cookies</h4>
<ul>
    <li>proper Website operation;</li>
    <li>personalization;</li>
    <li>analytics;</li>
    <li>marketing.</li>
</ul>

<h4 id="cookies-5-3">5.3. Legal Basis for Processing</h4>
<p>
Cookie processing is carried out based on the user's consent,
expressed through continued use of the Website.
</p>

<h4 id="cookies-5-4">5.4. Opting Out of Cookies</h4>
<p>
The user may disable cookies in browser settings,
which may limit Website functionality.
</p>

<hr />

<h3 id="third-parties">6. Sharing Personal Data with Third Parties</h3>

<h4 id="third-parties-6-1">6.1. Basis for Sharing</h4>
<p>
Data is shared for the purposes specified in this Policy.
</p>

<h4 id="third-parties-6-2">6.2. List of Third Parties</h4>
<table border="1" cellpadding="6">
    <tr>
        <th>Third Party</th>
        <th>Purpose</th>
        <th>Address</th>
    </tr>
    <tr>
        <td>Yandex LLC</td>
        <td>Web analytics (Yandex Metrica)</td>
        <td>Russia, Moscow, 16 Lva Tolstogo St.</td>
    </tr>
</table>

<hr />

<h3 id="cross-border">7. Cross-Border Personal Data Transfer</h3>

<h4 id="cross-border-7-1">7.1. General Provisions</h4>
<p>
The Company processes data on the territory of the UAE.
</p>

<h4 id="cross-border-7-2">7.2. Data Localization</h4>
<p>
Data processing is carried out in accordance with applicable UAE legislation.
</p>

<h4 id="cross-border-7-3">7.3. UAE Legislation</h4>
<p>
Federal Decree Law No. 45 of 2021 Regarding the Protection of Personal Data.
</p>

<hr />

<h3 id="protection">8. Personal Data Protection and Liability</h3>

<h4 id="protection-8-1">8.1. Confidentiality</h4>
<p>
Personal data is treated as confidential information.
</p>

<h4 id="protection-8-2">8.2. Protection Measures</h4>
<p>
The Company applies technical and organizational measures to protect data.
</p>

<h4 id="protection-8-3">8.3. Limitation of Liability</h4>
<p>
The Company is not liable for third-party websites or user actions.
</p>

<hr />

<h3 id="user-rights">9. User Rights</h3>

<h4 id="user-rights-9-1">9.1. List of Rights</h4>
<ul>
    <li>access to data;</li>
    <li>correction;</li>
    <li>withdrawal of consent;</li>
    <li>data deletion;</li>
    <li>appeal against Company actions.</li>
</ul>

<h4 id="user-rights-9-2">9.2. Exercising Rights</h4>
<p>
Requests should be sent to e-mail:
<a href="mailto:info@wbbs.ae">info@wbbs.ae</a>
</p>

<hr />

<h3 id="minors">10. Minors</h3>
<p>
The Website is not intended for persons under 18 years of age.
Personal data of minors is subject to deletion.
</p>

<hr />

<h3 id="changes">11. Policy Changes</h3>
<p>
The new version of the Policy takes effect upon publication on the Website.
</p>

<hr />

<h3 id="intellectual-property">12. Intellectual Property</h3>
<p>
All rights to the Website belong to the Company.
Copying and automated data collection are prohibited.
</p>
`;

const content = {
  en: POLICY_EN,
  ru: POLICY_RU,
};

const navContent = {
  ru: LEGALS_NAV_RU,
  en: LEGALS_NAV_EN,
};

const LegalsDubaiExpress = ({ handleLink }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { isTablet } = useGlobal();

  useEffect(() => {
    setTimeout(() => {
      const anchor = window.location.hash.slice(1);
      handleLink(anchor);
    }, 0);
  }, []);

  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  const text = content[lang as keyof typeof content] || content.en;
  const legalsNav =
    navContent[lang as keyof typeof navContent] || navContent.en;

  return (
    <>
      <MobileHeaderLayout content={<MobileHeaderTitle text={t('legals')} />} />
      <div className="content-layout legals">
        <div className={styles.root}>
          {!isTablet && (
            <>
              <Breadcrumbs
                items={
                  [
                    {
                      text: t('home'),
                      link: '/',
                    },
                    {
                      text: t('legals'),
                      link: '/legals',
                    },
                  ].filter(Boolean) as BreadcrumbsItem[]
                }
              />

              <h1 className={styles.title} style={{ marginBottom: 20 }}>
                {t('legals')}
              </h1>
            </>
          )}

          <div className={styles.page}>
            <div className={styles.page__links_container}>
              <ul className={styles.page__links}>
                {legalsNav.map((i) => (
                  <li className={styles.page__links_item} key={i.link}>
                    <a
                      href={`#${i.link}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleLink(i.link);
                      }}
                    >
                      {i.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.page__content}>
              <ReactMarkdown
                className={mdStyles.mdStyles}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LegalsDubaiExpress;
